import {
  createTargetPlan,
  modifyTargetPlanWeekdays,
} from '../api/workoutApi';

function collectPlansFromWeekdayDetails(weekdayDetails) {
  if (!weekdayDetails) return [];
  return Object.values(weekdayDetails).flatMap((day) => day?.plans || []);
}

function findReusablePlanId(existingPlans, templateId) {
  if (!templateId || !existingPlans?.length) return null;
  const match = existingPlans.find(
    (p) =>
      String(p.template_id) === String(templateId) &&
      (p.target_plan_id || p.plan_id || p.id)
  );
  return match?.target_plan_id || match?.plan_id || match?.id || null;
}

function titleAlreadyExistsMessage(message) {
  return /already exists/i.test(message || '');
}

function buildTitleCandidates(templateName, weekdayName, weekdayId) {
  const baseTitle = (templateName || 'Workout plan').trim();
  const dayLabel = (weekdayName || '').trim() || (weekdayId != null ? String(weekdayId) : '');
  const candidates = [baseTitle];
  if (dayLabel) {
    candidates.push(`${baseTitle} (${dayLabel})`);
  }
  candidates.push(`${baseTitle} (${dayLabel || 'day'} · ${Date.now()})`);
  return [...new Set(candidates.filter(Boolean))];
}

async function createPlanWithTitle(post, {
  eventTypeId,
  assigneeId,
  templateId,
  title,
  description,
}) {
  return createTargetPlan(post, {
    event_type_id: eventTypeId,
    target_plan: {
      operation: 'ADD',
      id: null,
      template_id: templateId,
      assigned_to_id: assigneeId,
      title,
      description,
      priority_order: 0,
    },
  });
}

/**
 * Assign a template to a weekday within a duration.
 * Reuses an existing target plan in this duration when the same template is
 * already linked (avoids user-scoped title collisions on modify-target-plans).
 */
export async function assignTemplateToWeekday({
  post,
  eventTypeId,
  assigneeId,
  durationId,
  weekdayId,
  weekdayName = '',
  templateId,
  templateName,
  planDescription = '',
  existingPlans = [],
  weekdayDetails = null,
  forceNewPlan = false,
}) {
  if (weekdayId == null || weekdayId === '') {
    return { success: false, message: 'Weekday is required to assign a workout.' };
  }
  if (!durationId) {
    return { success: false, message: 'Duration is required to assign a workout.' };
  }

  const plans =
    existingPlans.length > 0
      ? existingPlans
      : collectPlansFromWeekdayDetails(weekdayDetails);

  let planId = forceNewPlan ? null : findReusablePlanId(plans, templateId);

  if (!planId) {
    const description =
      planDescription ||
      `Plan from template: ${templateName || 'Workout plan'}`;
    const titles = buildTitleCandidates(templateName, weekdayName, weekdayId);
    let planResult = null;

    for (const title of titles) {
      planResult = await createPlanWithTitle(post, {
        eventTypeId,
        assigneeId,
        templateId,
        title,
        description,
      });
      if (planResult.success) break;
      if (!titleAlreadyExistsMessage(planResult.message)) break;
    }

    if (!planResult?.success) {
      return {
        success: false,
        message: planResult?.message || 'Failed to create plan',
      };
    }

    planId = planResult.data?.data?.id;
    if (!planId) {
      return { success: false, message: 'Plan created but no ID returned.' };
    }
  }

  const weekdayResult = await modifyTargetPlanWeekdays(post, {
    event_type_id: eventTypeId,
    target_plan_weekdays: [
      {
        operation: 'ADD',
        id: null,
        target_plan_id: planId,
        target_plan_duration_id: durationId,
        weekday_id: weekdayId,
      },
    ],
  });

  if (!weekdayResult.success) {
    return {
      success: false,
      message: weekdayResult.message || 'Failed to assign weekday',
    };
  }

  return { success: true, planId };
}
